import { useState, useEffect, useCallback } from 'react';

// Get tenant_id from environment variable or use default
const TENANT_ID = import.meta.env.VITE_TENANT_ID || "default";

// Use the new tenant-assets bucket URL
const SUPABASE_EVENT_JSON_URL = import.meta.env.VITE_SUPABASE_EVENT_JSON_URL || 
  `https://isinlnouhjjlsvumriyg.supabase.co/storage/v1/object/public/tenant-assets/event-syncs/${TENANT_ID}/events-sync-${TENANT_ID}-latest.json`;

export interface EventItem {
  id: string;
  title: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  date_start: string;
  date_end: string;
  instagram?: string;
  flyer_url?: string;
  event_type?: 'Festival' | 'Exclusive Training' | 'Other' | null;
  region?: string;
}

export interface UseEventsDataOptions {
  enableCache?: boolean;
  cacheDuration?: number; // in milliseconds
  enableRealtime?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
}

const DEFAULT_OPTIONS: Required<UseEventsDataOptions> = {
  enableCache: true,
  cacheDuration: 60 * 60 * 1000, // 1 hour
  enableRealtime: false,
  autoRefresh: false,
  refreshInterval: 300000, // 5 minutes
};

export function useEventsData(options: UseEventsDataOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);

  // Fetch events from API
  const fetchEvents = useCallback(async (skipCache = false) => {
    // Include tenant_id in cache key to prevent cross-tenant cache issues
    const tenantId = import.meta.env.VITE_TENANT_ID || "default";
    const CACHE_KEY = `events_map_data_${tenantId}`;
    const CACHE_TIMESTAMP_KEY = `events_map_timestamp_${tenantId}`;

    // Check cache first if enabled and not skipping
    if (opts.enableCache && !skipCache) {
      const cachedTimestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
      const cachedData = sessionStorage.getItem(CACHE_KEY);

      if (cachedTimestamp && cachedData) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();
        const isCacheValid = now - timestamp < opts.cacheDuration;

        if (isCacheValid) {
          try {
            const parsedEvents = JSON.parse(cachedData);
            
            // Skip cache if it contains no events - always retry from source
            if (parsedEvents.length === 0) {
              // Don't return early - continue to fetch from API
            } else {
              setEvents(parsedEvents);
              setLoading(false);
              setError(null);
              setLastFetchTime(timestamp);
              return; // Only return if cache has actual events
            }
        } catch (err) {
          // Failed to parse cached events data
        }
        }
      }
    }

    // Fetch fresh data from API with cache-busting query parameter
    try {
      // Add timestamp query parameter to bust browser/CDN cache
      const url = new URL(SUPABASE_EVENT_JSON_URL);
      url.searchParams.set('_t', Date.now().toString());
      const response = await fetch(url.toString(), {
        cache: 'no-store', // Force no cache
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch events: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Extract events array from different possible structures
      let eventsData = [];
      if (Array.isArray(data)) {
        eventsData = data;
      } else if (data.events && Array.isArray(data.events)) {
        // Handle structure: { success: true, count: 7, events: [...] }
        eventsData = data.events;
      } else if (data.data && Array.isArray(data.data)) {
        // Handle structure: { data: [...] }
        eventsData = data.data;
      } else {
        eventsData = [];
      }
      
      // Normalize field names to match frontend expectations
      // Convert: dateStart -> date_start, dateEnd -> date_end, flyerUrl -> flyer_url, eventType -> event_type
      const normalizedEvents = eventsData.map((event: any) => ({
        ...event,
        date_start: event.date_start || event.dateStart,
        date_end: event.date_end || event.dateEnd,
        flyer_url: event.flyer_url || event.flyerUrl,
        event_type: event.event_type || event.eventType,
      }));
      
      // Process and enrich events
      const enrichedEvents = normalizedEvents.map((event: any) => ({
        ...event,
        latitude: Number(event.latitude) || 0,
        longitude: Number(event.longitude) || 0,
      }));

      // Cache the data if enabled and we have events
      if (opts.enableCache && enrichedEvents.length > 0) {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(enrichedEvents));
        sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } else if (opts.enableCache && enrichedEvents.length === 0) {
        // Clear cache if API returns empty to force retry on next load
        sessionStorage.removeItem(CACHE_KEY);
        sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
      }

      setEvents(enrichedEvents);
      setLoading(false);
      setError(null);
      setLastFetchTime(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      setLoading(false);
      
      // If cache exists but is expired, try to use it as fallback
      if (opts.enableCache && sessionStorage.getItem(CACHE_KEY)) {
        try {
          const cachedData = sessionStorage.getItem(CACHE_KEY);
          if (cachedData) {
            const parsedEvents = JSON.parse(cachedData);
            setEvents(parsedEvents);
          }
        } catch (parseErr) {
          // Failed to parse fallback cache
        }
      }
    }
  }, [opts.enableCache, opts.cacheDuration]);

  // Initial fetch
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Auto-refresh if enabled
  useEffect(() => {
    if (!opts.autoRefresh || !opts.refreshInterval) return;

    const intervalId = setInterval(() => {
      fetchEvents(true); // Skip cache for auto-refresh
    }, opts.refreshInterval);

    return () => clearInterval(intervalId);
  }, [opts.autoRefresh, opts.refreshInterval, fetchEvents]);

  // Manual refresh function
  const refresh = useCallback(() => {
    return fetchEvents(true);
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    lastFetchTime,
    refresh,
  };
}
